import type { CustomerId } from "types/customer"
import type { MyCustomersResponse } from "api/useMyCustomers"

type BootInputs = {
  // GET /me/customers answered (success or error)
  myCustomersSettled: boolean
  memberships: MyCustomersResponse | undefined
  // GET /me/profile answered (success or error)
  profileSettled: boolean
}

/**
 * Whether the active customer can be resolved yet. /me/customers is the
 * source of truth, so the profile (/me/profile) is only waited for when the
 * fallback needs its default-customer claim: /me/customers failed or knows no
 * customer. On the normal path the two requests run side by side and neither
 * waits for the other.
 */
export const canResolveCustomer = ({ myCustomersSettled, memberships, profileSettled }: BootInputs): boolean => {
  if (!myCustomersSettled) return false
  if (memberships && memberships.customers.length > 0) return true
  return profileSettled
}

type ResolveInputs = {
  memberships: MyCustomersResponse | undefined
  // the customer an emailed link points at (?customer=)
  linkedCustomer: CustomerId | null
  // the token's default customer, from /me/profile app_metadata
  profileCustomerId: CustomerId | null | undefined
  defaultCustomerId: CustomerId
}

export type ResolvedCustomer = {
  customerId: CustomerId
  // the link moved the user: remember it server-side like a picker switch
  persistLinked: boolean
  source: "memberships" | "profile" | "default"
}

/**
 * The customer a fresh session opens. Precedence: the customer an emailed link
 * points at (only when the account may use it), the selection persisted by
 * the picker (rr-api), the token's home customer, then the first customer the
 * account may use. Without a usable /me/customers answer: the profile's
 * default customer, else the app default.
 */
export const resolveCustomer = ({ memberships, linkedCustomer, profileCustomerId, defaultCustomerId }: ResolveInputs): ResolvedCustomer => {
  if (memberships && memberships.customers.length > 0) {
    const ids = memberships.customers.map((customer) => customer.customer_id)
    const fromLink = linkedCustomer && ids.includes(linkedCustomer) ? linkedCustomer : null
    const candidates: (CustomerId | null | undefined)[] = [fromLink, memberships.current_customer_id, memberships.home_customer_id, ids[0]]
    const chosen = candidates.find((id): id is CustomerId => !!id && ids.includes(id)) ?? ids[0]
    return { customerId: chosen, persistLinked: !!fromLink && fromLink !== memberships.current_customer_id, source: "memberships" }
  }
  if (profileCustomerId) return { customerId: profileCustomerId, persistLinked: false, source: "profile" }
  return { customerId: defaultCustomerId, persistLinked: false, source: "default" }
}
